import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin-OR-approved-IP button that sends the current race's exotic tickets to the
// BLRPost ASP endpoint. Shows a confirmation dialog with the exact csv_data first.
export function AdwButton({ track, race, date, getPools, getKeys, className }) {
  const [eligible, setEligible] = useState(false);
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [warning, setWarning] = useState("");
  const [avoid, setAvoid] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/quickpick/adw-eligibility`, { withCredentials: true })
      .then((r) => { if (alive) setEligible(!!r.data?.eligible); })
      .catch(() => { if (alive) setEligible(false); });
    return () => { alive = false; };
  }, []);

  if (!eligible) return null;

  // Safety: disable ADW when viewing a PAST date (races already run) — ET-based.
  const etToday = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  const isPast = !!date && String(date) < etToday;

  const body = () => {
    const keys = getKeys ? (getKeys() || []).map(String) : [];
    return { track, race: Number(race), date, pools: getPools(), ...(keys.length ? { keys } : {}) };
  };

  const preview = async () => {
    if (isPast) return;
    const p = body();
    if (!p.pools || !Object.keys(p.pools).length) { toast.error("No tickets to send"); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/quickpick/adw-send`, { ...p, preview: true }, { withCredentials: true });
      setFiles(r.data?.files || []);
      setWarning(r.data?.warning || "");
      setAvoid(r.data?.avoid || []);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build ADW wager");
    } finally { setLoading(false); }
  };

  const fire = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/quickpick/adw-send`, { ...body(), preview: false }, { withCredentials: true });
      const results = r.data?.files || [];
      results.forEach((f) => {
        if (f.result?.ok) toast.success(`${f.label} sent (${f.combos} combos) — HTTP ${f.result.status_code}`);
        else toast.error(`${f.label} failed — ${f.result?.error || ("HTTP " + f.result?.status_code)}`);
      });
      if (results.every((f) => f.result?.ok)) setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ADW send failed");
    } finally { setSending(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={preview} disabled={loading || isPast}
        title={isPast ? "Past date — ADW disabled to prevent accidental wagers" : undefined}
        data-testid="adw-send-btn"
        className={className || "h-7 px-2 text-xs border-rose-400 text-rose-600 hover:bg-rose-50"}>
        {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}ADW
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="adw-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm live ADW wager — {track} R{race}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[52vh] overflow-auto">
            <p className="text-sm font-semibold text-rose-600" data-testid="adw-warning">
              This sends REAL wagers to BLRPost. Review each file before sending:
            </p>
            {warning && (
              <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-300 text-amber-800 rounded-lg px-3 py-2" data-testid="adw-safety-warning">
                <span className="font-bold">⚠</span><span>{warning}</span>
              </div>
            )}
            {avoid.length > 0 && (
              <div className="text-sm bg-rose-50 border border-rose-300 text-rose-800 rounded-lg px-3 py-2" data-testid="adw-avoid-warning">
                <div className="font-bold mb-1">⚠ Avoid list — a keyed horse has a listed trainer/jockey:</div>
                <ul className="list-disc ml-5">{avoid.map((a, i) => <li key={i}>{a}</li>)}</ul>
                <div className="mt-1 text-xs">Auto Tri would HOLD this play. Send anyway only if you're sure.</div>
              </div>
            )}
            {files.map((f, i) => (
              <div key={i} data-testid={`adw-file-${f.label}`}>
                <div className="text-xs font-semibold text-slate-700 mb-1">{f.label} · {f.combos} combos</div>
                <pre className="bg-slate-900 text-slate-100 text-xs p-3 rounded-lg whitespace-pre-wrap break-all">{f.csv_data}</pre>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={sending} data-testid="adw-cancel-btn">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-500 text-white" onClick={fire} disabled={sending} data-testid="adw-confirm-send-btn">
              {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}Send to ASP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AdwButton;
